import { getErrorMessage } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BodyLargeEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckboxWithLabel,
    ControlledInput,
    Form,
    isWeb,
    SafeAreaView,
    ScrollView,
    SegmentedControl,
    SelectDropdown,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { getRandomNumber, isEmpty } from '../../../common/misc';
import { captureException, captureMessage } from '../../../logger/Logger';
import useCreateTransactionSimulation, {
    type CreateTransactionSimulationRequest,
    type TransactionSimulationData,
} from '../../hooks/transaction/useCreateTransactionSimulation';
import {
    ATM_WITHDRAWAL_EXAMPLES,
    CARD_TRANSACTION_EXAMPLES,
} from '../../types/TransactionSimulation';

enum SimulationTab {
    CARD_TRANSACTION = 'card_transaction',
    ATM_WITHDRAWAL = 'atm_withdrawal',
}

const transactionSimulationSchema = z.object({
    cardId: z.string(),
    amount: z.string().min(1, 'Please enter an amount'),
    json: z.string(),
});

type TransactionSimulationSchema = z.infer<typeof transactionSimulationSchema>;

interface SimulateCardTransactionProps {
    cardId: string;
    submit: (
        data: CreateTransactionSimulationRequest,
        clearTransaction?: boolean,
    ) => void;
    isSubmitting: boolean;
}

const SimulateCardTransaction = ({
    cardId,
    submit,
    isSubmitting,
}: SimulateCardTransactionProps) => {
    const jsonOptions = CARD_TRANSACTION_EXAMPLES.map(({ name }) => name);
    const [selectedJsonOption, setSelectedJsonOption] = useState(
        jsonOptions[0],
    );
    const [clearTransaction, setClearTransaction] = useState(false);

    const {
        setValue,
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<TransactionSimulationSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(transactionSimulationSchema),
        defaultValues: {
            cardId,
            amount: getRandomNumber(1, 10000).toString(),
            json: '',
        },
    });

    useEffect(() => {
        if (!isEmpty(selectedJsonOption)) {
            const example = CARD_TRANSACTION_EXAMPLES.find(
                ({ name }) => name === selectedJsonOption,
            );
            setValue('json', JSON.stringify(example?.json, null, 4));
        } else {
            setValue('json', '');
        }
    }, [selectedJsonOption]);

    const onSubmit: SubmitHandler<TransactionSimulationSchema> = ({
        cardId,
        amount,
        json,
    }) => {
        const data: Partial<TransactionSimulationData> = {
            ...(JSON.parse(json) as Partial<TransactionSimulationData>),
            amount: parseInt(amount, 10),
        };

        submit(
            {
                cardId,
                data,
                path: '/authorization',
            },
            clearTransaction,
        );
    };

    return (
        <ScrollView>
            <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                <YStack gap={12} flex={1}>
                    <ControlledInput
                        name="cardId"
                        label="Card ID"
                        control={control}
                        register={register}
                        errors={errors}
                        editable={false}
                        disabled
                    />
                    <ControlledInput
                        name="amount"
                        label="Amount (cents)"
                        control={control}
                        register={register}
                        errors={errors}
                        type="numeric"
                        inputMode="numeric"
                    />
                    <SelectDropdown
                        defaultValue={jsonOptions[0]}
                        options={jsonOptions}
                        onSelect={(selectedOption) => {
                            setSelectedJsonOption(selectedOption);
                        }}
                    />
                    <ControlledInput
                        name="json"
                        label="JSON"
                        control={control}
                        register={register}
                        errors={errors}
                        multiline
                        height={200}
                    />
                    <CheckboxWithLabel
                        id="clear-simulated-transaction-checkbox"
                        size="large"
                        label="Clear transaction"
                        checked={clearTransaction}
                        onPress={() => {
                            setClearTransaction((prev) => !prev);
                        }}
                    />
                    <Button
                        tone="positive"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Submit authorization"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isSubmitting}
                    />
                </YStack>
            </Form>
        </ScrollView>
    );
};

const SimulateAtmWithdrawal = ({
    cardId,
    submit,
    isSubmitting,
}: SimulateCardTransactionProps) => {
    const jsonOptions = ATM_WITHDRAWAL_EXAMPLES.map(({ name }) => name);
    const [selectedJsonOption, setSelectedJsonOption] = useState(
        jsonOptions[0],
    );

    const {
        setValue,
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<TransactionSimulationSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(transactionSimulationSchema),
        defaultValues: {
            cardId,
            amount: getRandomNumber(1, 10000).toString(),
            json: '',
        },
    });

    useEffect(() => {
        if (!isEmpty(selectedJsonOption)) {
            const example = ATM_WITHDRAWAL_EXAMPLES.find(
                ({ name }) => name === selectedJsonOption,
            );
            setValue('json', JSON.stringify(example?.json, null, 4));
        } else {
            setValue('json', '');
        }
    }, [selectedJsonOption]);

    const onSubmit: SubmitHandler<TransactionSimulationSchema> = ({
        cardId,
        amount,
        json,
    }) => {
        const data: Partial<TransactionSimulationData> = {
            ...(JSON.parse(json) as Partial<TransactionSimulationData>),
            amount: parseInt(amount, 10),
        };

        submit({
            cardId,
            data,
            path: '/financial/withdrawal',
        });
    };

    return (
        <ScrollView>
            <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                <YStack gap={12} flex={1}>
                    <ControlledInput
                        name="cardId"
                        label="Card ID"
                        control={control}
                        register={register}
                        errors={errors}
                        editable={false}
                        disabled
                    />
                    <ControlledInput
                        name="amount"
                        label="Amount (cents)"
                        control={control}
                        register={register}
                        errors={errors}
                        type="numeric"
                        inputMode="numeric"
                    />
                    <SelectDropdown
                        defaultValue={jsonOptions[0]}
                        options={jsonOptions}
                        onSelect={(selectedOption) => {
                            setSelectedJsonOption(selectedOption);
                        }}
                    />
                    <ControlledInput
                        name="json"
                        label="JSON"
                        control={control}
                        register={register}
                        errors={errors}
                        multiline
                        height={200}
                    />
                    <Button
                        tone="positive"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Withdraw cash"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isSubmitting}
                    />
                </YStack>
            </Form>
        </ScrollView>
    );
};

const SimulateTransaction = () => {
    const { cardId } = useLocalSearchParams<{
        cardId: string;
    }>();

    const toast = useToastController();

    const [index, setIndex] = useState(0);
    const [simulationTab, setSimulationTab] = useState(
        SimulationTab.CARD_TRANSACTION,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const routes = Object.values(SimulationTab).map((tab) => ({ key: tab }));

    const { mutateAsync: createTransactionSimulation } =
        useCreateTransactionSimulation();

    const submit = async (
        body: CreateTransactionSimulationRequest,
        clearTransaction?: boolean,
    ) => {
        setIsSubmitting(true);

        try {
            const authorizationResponse = await createTransactionSimulation({
                body,
            });

            toast.show('Created transaction!', { duration: 2000 });
            captureMessage(
                `Created simulated transaction: ${JSON.stringify(authorizationResponse)}`,
            );

            if (clearTransaction) {
                const { token } = authorizationResponse.transaction;
                const { amount, card_acceptor, mid, network_fees } = body.data;

                const clearingResponse = await createTransactionSimulation({
                    body: {
                        cardId,
                        path: '/clearing',
                        data: {
                            amount,
                            card_acceptor,
                            mid,
                            network_fees,
                            original_transaction_id: token,
                        },
                    },
                });

                toast.show('Cleared transaction!', { duration: 2000 });
                captureMessage(
                    `Cleared simulated transaction: ${JSON.stringify(clearingResponse)}`,
                );
            }
        } catch (e) {
            captureException(e);
            toast.show(`Error! ${getErrorMessage(e)}`, {
                duration: 2000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (cardId == null) {
        captureException(
            new Error('Missing cardId query param for transaction simulation'),
        );
        router.back();
    }

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: SimulationTab } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        switch (route.key) {
            case SimulationTab.CARD_TRANSACTION:
                return (
                    <SimulateCardTransaction
                        cardId={cardId}
                        submit={submit}
                        isSubmitting={isSubmitting}
                    />
                );
            case SimulationTab.ATM_WITHDRAWAL:
                return (
                    <SimulateAtmWithdrawal
                        cardId={cardId}
                        submit={submit}
                        isSubmitting={isSubmitting}
                    />
                );
            default:
                return <></>;
        }
    };

    return (
        <SafeAreaView>
            <YStack
                maxWidth={700}
                alignSelf="center"
                marginTop={isWeb ? 24 : 0}
                marginHorizontal={16}
            >
                <BodyLargeEmphasized alignSelf="center">
                    Simulate Transaction
                </BodyLargeEmphasized>
                <View marginTop={-26} marginBottom={24}>
                    <BackButton />
                </View>
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderTabBar={() => (
                        <View marginBottom={16}>
                            <SegmentedControl
                                options={[
                                    {
                                        label: 'Card transaction',
                                        value: SimulationTab.CARD_TRANSACTION,
                                    },
                                    {
                                        label: 'ATM withdrawal',
                                        value: SimulationTab.ATM_WITHDRAWAL,
                                    },
                                ]}
                                selected={simulationTab}
                                defaultValue={SimulationTab.CARD_TRANSACTION}
                                onValueChange={(simulationTab) => {
                                    setSimulationTab(simulationTab);
                                    setIndex(
                                        simulationTab ===
                                            SimulationTab.CARD_TRANSACTION
                                            ? 0
                                            : 1,
                                    );
                                }}
                            />
                        </View>
                    )}
                    renderScene={renderScene}
                    onIndexChange={setIndex}
                />
            </YStack>
        </SafeAreaView>
    );
};

export default SimulateTransaction;
